import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { updateTimesheet } from "../../redux/actions/timesheet.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { TimePicker } from "antd";
//import type { Dayjs } from 'dayjs';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import { storeStateType } from "../../redux/store";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  filterData: any;
}

const formSchema = object({
  id: z.string(),
  shift_start_date: z.string().min(1, "Shift Start Date is required."),
  shift_end_date: z.string().min(1, "Shift End Date is required."),
  shift_start_time: z.string().min(1, "Shift End Date is required."),
  shift_end_time: z.string().min(1, "Emd time is required."),
});

type FormInput = TypeOf<typeof formSchema>;

const defaultValues = {
  shift_start_date: "",
  shift_start_time: "",
  shift_end_date: "",
  shift_end_time: "",
};

export function TimesheetEditForm(props: Props) {
  const { handleCloseDialog } = props;
  const dispatch = useDispatch();
  const { timesheet, save } = useSelector(
    (state: storeStateType) => state.employeetimesheet
  );
  const loading = useSelector((state: storeStateType) => state.UI.loading);
  const {
    register,
    reset,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });

  //const[defautStartTime, setdefautStartTime] = useState(dayjs.extend('13:30:56', 'HH:mm:ss'))

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
  }, [handleCloseDialog, reset]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (timesheet)
      if (timesheet?.id) {
        setValue("id", `${timesheet.id}`);
      }

    if (timesheet?.shift_start_date) {
      setValue(
        "shift_start_date",
        moment(timesheet.shift_start_date).format("YYYY-MM-DD")
      );
    }

    if (timesheet?.shift_start_time) {
      setValue("shift_start_time", timesheet.shift_start_time);
    }

    if (timesheet?.shift_end_date) {
      setValue(
        "shift_end_date",
        moment(timesheet.shift_end_date).format("YYYY-MM-DD")
      );
    }

    if (timesheet?.shift_end_time) {
      setValue("shift_end_time", timesheet.shift_end_time);
    }
  }, [timesheet, setValue]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("shift_start_date", data.shift_start_date);
    formData.append("shift_start_time", data.shift_start_time);
    formData.append("shift_end_date", data.shift_start_date);
    formData.append("shift_end_time", data.shift_end_time);
    formData.append("id", data.id as unknown as string);

    if (data.id) {
      dispatch<any>(updateTimesheet(formData, props.filterData));
    }
  };
  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.action}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="companyform"
          name="companyform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <input type="hidden" {...register("id")} />
          <TextField
            disabled
            id="shift_start_date"
            margin="dense"
            fullWidth
            label="Shift Start Date"
            placeholder="--"
            required
            type="date"
            inputProps={{
              max: moment().format("YYYY-MM-DD"),
            }}
            variant="standard"
            defaultValue=""
            {...register("shift_start_date")}
            error={!!errors["shift_start_date"]}
            helperText={
              errors["shift_start_date"]
                ? errors["shift_start_date"].message
                : ""
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Controller
            control={control}
            name="shift_start_time"
            render={({ field: { onChange, value } }) => (
              <>
                <FormLabel sx={{ fontSize: "12px" }}>ClockIn *</FormLabel>
                <TimePicker
                  placeholder="Start Time"
                  onChange={(time, timestring) => {
                    onChange(timestring);
                  }}
                  status={!!errors["shift_start_time"] ? "error" : ""}
                  value={moment(value, "HH:mm:ss")}
                  className="ant-time-picker"
                />
                <FormHelperText error={!!errors["shift_start_time"]}>
                  {errors["shift_start_time"]
                    ? errors["shift_start_time"].message
                    : ""}
                </FormHelperText>
              </>
            )}
          />
          <TextField
            disabled
            id="shift_end_date"
            margin="dense"
            fullWidth
            label="Shift End Date"
            placeholder="--"
            required
            type="date"
            inputProps={{
              max: moment().format("YYYY-MM-DD"),
            }}
            variant="standard"
            defaultValue=""
            {...register("shift_end_date")}
            error={!!errors["shift_end_date"]}
            helperText={
              errors["shift_end_date"] ? errors["shift_end_date"].message : ""
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Controller
            control={control}
            name="shift_end_time"
            render={({ field: { onChange, value } }) => (
              <>
                <FormLabel sx={{ fontSize: "12px" }}>ClockOut *</FormLabel>
                <TimePicker
                  placeholder="End Time"
                  onChange={(time, timestring) => {
                    onChange(timestring);
                  }}
                  value={moment(value, "HH:mm:ss")}
                  status={!!errors["shift_end_time"] ? "error" : ""}
                  className="ant-time-picker"
                />
                <FormHelperText error={!!errors["shift_end_time"]}>
                  {errors["shift_end_time"]
                    ? errors["shift_end_time"].message
                    : ""}
                </FormHelperText>
              </>
            )}
          />
          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="companyform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
